// The context the alignment hooks read, built once per scoring from the
// level and the play: which alignment each planet has, which are in force
// (a constellation surrounds the planet right now), and the committed sky.
import { SKY_HEIGHT, skyWidth } from '../level/presets';
import { planetsOf, type PlayLevel } from '../level/schema';
import { derive } from '../play/state';
import type { AlignmentId } from './catalogue';
import type { ActiveAlignment, AlignmentContext, ScoreInput } from './types';

export function buildContext(level: PlayLevel, input: ScoreInput): AlignmentContext {
  const constellations = input.constellations;
  const derived = derive({ level, constellations: [...constellations], selection: [], selectionLines: [], alignments: [], removals: 0 });
  const chosen = new Map<number, AlignmentId>();
  for (const choice of input.alignments ?? []) chosen.set(choice.planet, choice.alignment);
  const planets = new Map(planetsOf(level).map((p) => [p.id, p]));
  const active = new Map<number, ActiveAlignment>();
  constellations.forEach((c, index) => {
    if (!c.planet) return;
    const planet = planets.get(c.planet.id);
    const alignment = chosen.get(c.planet.id);
    if (!planet || alignment === undefined) return;
    active.set(planet.id, { planet, alignment, word: c.word, index });
  });
  const inForce = new Set<AlignmentId>();
  for (const a of active.values()) inForce.add(a.alignment);
  return {
    level,
    constellations,
    derived,
    chosen,
    active,
    inForce,
    removals: input.removals ?? 0,
    sky: { width: skyWidth(level.sky.preset), height: SKY_HEIGHT },
    unused: level.nodes.length - derived.locked.size,
  };
}

/** The same context with one planet's alignment out of force, to measure what it adds. */
export function withoutPlanet(ctx: AlignmentContext, planetId: number): AlignmentContext {
  const active = new Map(ctx.active);
  active.delete(planetId);
  const inForce = new Set<AlignmentId>();
  for (const a of active.values()) inForce.add(a.alignment);
  return { ...ctx, active, inForce };
}
